import { useState } from "react";
import { useGetUtilizationReportQuery, useLazyGetUtilizationReportCsvQuery } from "../services/reportsV2Api";
import { ReportFilters } from "../components/ReportFilters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, XCircle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";

export default function LocationUtilization() {
  const [filters, setFilters] = useState({});
  const { data: report, isLoading } = useGetUtilizationReportQuery(filters);
  const [triggerExportCsv, { isLoading: isExporting }] = useLazyGetUtilizationReportCsvQuery();

  const handleExportCsv = async (exportFilters: any) => {
    try {
      const blob = await triggerExportCsv(exportFilters).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Location_Utilization_${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV export started");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Location Utilization</h1>
          <p className="text-muted-foreground">
            Monitor clinic activity, visit volumes, and no-show trends.
          </p>
        </div>
      </div>

      <ReportFilters 
        onSearch={setFilters} 
        onExportCsv={handleExportCsv}
      />

      <Card>
        <CardContent className="p-0">
          {(isLoading || isExporting) ? (
            <div className="flex flex-col justify-center items-center py-20 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              {isExporting && <p className="text-sm text-muted-foreground">Preparing your download...</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Vendor</TableHead>
                    <TableHead>Clinic Name</TableHead>
                    <TableHead>City, State</TableHead>
                    <TableHead className="text-center">Total Visits</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">Cancelled</TableHead>
                    <TableHead className="text-center">No-Shows</TableHead>
                    <TableHead className="text-right">Completion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report?.details.map((detail, idx) => {
                    const rate = detail.totalVisits > 0 
                      ? Math.round((detail.completedCount / detail.totalVisits) * 100) 
                      : 0;

                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium text-muted-foreground">
                          {detail.vendorName}
                        </TableCell>
                        <TableCell className="font-semibold">{detail.clinicName}</TableCell>
                        <TableCell>{detail.city}, {detail.state}</TableCell>
                        <TableCell className="text-center font-bold">
                          {detail.totalVisits}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 border-emerald-100">
                           <CheckCircle2 className="w-3 h-3 mr-1" /> {detail.completedCount}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="bg-amber-50 text-amber-600 border-amber-100">
                          <XCircle className="w-3 h-3 mr-1" /> {detail.cancelledCount}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="bg-rose-50 text-rose-600 border-rose-100">
                          <XCircle className="w-3 h-3 mr-1" /> {detail.noShowCount}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                             <div className="w-24 bg-muted h-2 rounded-full overflow-hidden">
                               <div 
                                 className={cn(
                                   "h-full rounded-full transition-all",
                                   rate > 80 ? "bg-emerald-500" : rate > 50 ? "bg-amber-500" : "bg-rose-500"
                                 )}
                                 style={{ width: `${rate}%` }}
                               />
                             </div>
                             <span className="text-sm font-bold min-w-[35px]">{rate}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
